/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useState, useEffect } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import {
  EditorState,
  ContentState,
  convertToRaw,
  // convertFromHTML,
} from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import compose from 'lib/compose';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import * as I from 'immutable';

const getHTML = compose(
  draftToHtml,
  convertToRaw,
  (s) => s.getCurrentContent()
);

const getHTMLFromEditorState = (s) => {
  return s.getCurrentContent().hasText() ? getHTML(s) : '';
};

const getContentStateFromHTML = (value) => {
  const blocksFromHTML = htmlToDraft(value);
  const { contentBlocks, entityMap } = blocksFromHTML;
  if (!contentBlocks) return;
  return ContentState.createFromBlockArray(contentBlocks, entityMap);
};

const getStateForValue = (value) => {
  const initState = EditorState.createEmpty();
  if (!value) return initState;
  const contentState = getContentStateFromHTML(value);
  if (!contentState) return initState;
  return EditorState.createWithContent(contentState);
};

const getHTMLForValue = compose(
  getHTMLFromEditorState,
  getStateForValue
);

function isHTML(value) {
  return typeof value === 'string' && /<p>/.test(value.trim());
}

export default (props: {
  value: string,
  onChange?: (string) => any,
  name?: string,
  cssStyles?: string,
  cssContent?: string,
  placeholder?: string,
}): Element<string> => {
  const {
    name = '',
    value,
    onChange,
    cssStyles,
    placeholder,
    cssContent,
  } = props;
  const [state, setState] = useState(() => getStateForValue(value));
  const [html, setHTML] = useState(() =>
    isHTML(value) ? value : getHTMLForValue(value)
  );

  useEffect(() => {
    const newHTML = isHTML(value) ? value : getHTMLForValue(value);
    if (html !== newHTML) {
      setState(getStateForValue(value));
      setHTML(newHTML);
    }
  }, [value]);

  return (
    <div
      css={css(
        cssStyles || '',
        `
        padding: 6px;
        border: solid 1px #efefef;
        border-radius: 5px;
        min-height: 250px;
        .rdw-editor-wrapper, .DraftEditor-root, .DraftEditor-editorContainer, .public-DraftEditor-content {
        min-height: 250px;
        }
        .rdw-link-modal {
          height: initial;
        }
      `,
        cssContent || ''
      )}
    >
      <textarea
        value={html}
        name={name}
        style={{ display: 'none' }}
        onChange={() => {}}
      />
      <Editor
        key={html}
        editorState={state}
        placeholder={placeholder}
        toolbar={{
          options: [
            'inline',
            'fontFamily',
            'fontSize',
            'blockType',
            'list',
            'link',
            'colorPicker',
          ],
          inline: {
            options: ['bold', 'italic', 'underline'],
          },
        }}
        onEditorStateChange={(ns) => {
          if (!I.is(ns.getImmutable(), state.getImmutable())) {
            setState(ns);
            const newHTML = getHTMLFromEditorState(ns);
            setHTML(newHTML);
            onChange && onChange(newHTML);
          }
        }}
      />
    </div>
  );
};
